// src/components/TaskChat/hooks/useTaskChat.ts
import { useState, useEffect, useRef, useMemo } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from '../../firebase/AuthProvider';
import TaskAction from '../../interfaces/task/taskActions/TaskAction';
import { getChatsForTask } from '../../services/taskServices/TaskCommentsService';

export const useTaskChat = (taskId: string) => {
  const { currentUser } = useAuth();
  const [chats, setChats] = useState<TaskAction[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const connectionRef = useRef<signalR.HubConnection | null>(null);
  const receiveSound = useMemo(() => new Audio('/sounds/message-received.mp3'), []);

  // Effect for fetching initial chats
  useEffect(() => {
    const fetchTaskChats = async () => {
      if (taskId && currentUser) {
        setIsLoading(true);
        const authToken = await currentUser.getIdToken();
        const fetchedChats = await getChatsForTask(taskId, authToken);
        if (fetchedChats) {
          setChats(fetchedChats);
        }
        setIsLoading(false);
      }
    };
    fetchTaskChats();
  }, [taskId, currentUser]);

  // Effect for SignalR connection
  useEffect(() => {
    if (!currentUser || !taskId) return;

    const connect = async () => {
      const token = await currentUser.getIdToken();
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(`${process.env.REACT_APP_API_BASE_URL}/chatHub`, {
          accessTokenFactory: () => token,
        })
        .withAutomaticReconnect()
        .build();

        connection.on("ReceiveComment", (message: TaskAction) => {
            if (message.taskId == Number(taskId)) {
              // 🔊 Play sound only if the message is from another user
              if (currentUser && message.author?.id !== currentUser.uid) {
                receiveSound.play().catch(e => console.error("Error playing sound:", e));
              }
              
              // Ensure completionTime is set - use the one from message or current time
              const completionTime = message.completionTime 
                ? (typeof message.completionTime === 'string' ? new Date(message.completionTime) : message.completionTime)
                : new Date();
              
              // ✨ Flag the message as new to trigger the animation
              setChats((prevChats) => [...prevChats, { ...message, isNew: true, completionTime }]);
            }
          });

      try {
        await connection.start();
        console.log("SignalR connected");
      } catch (err) {
        console.error("SignalR connection error:", err);
      }
      connectionRef.current = connection;
    };

    connect();

    return () => {
      connectionRef.current?.stop();
    };
  }, [currentUser, taskId, receiveSound]);

  const sendMessage = async (message: string, userIds: string[]) => {
    if (connectionRef.current) {
      try {
        await connectionRef.current.invoke("SendTaskMessage", taskId, message, userIds);
      } catch (err) {
        console.error("SignalR SendTaskMessage error:", err);
      }
    }
  };

  return { chats, sendMessage, isLoading, setChats , setIsLoading };
};